import { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useEmailTemplates, type EmailTemplate } from '@/hooks/email/useEmailTemplates';

interface EmailTemplateCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (template: EmailTemplate) => void;
}

const MERGE_VARIABLES: Array<{ label: string; key: string }> = [
  { label: '{{nome}}', key: 'nome' },
  { label: '{{primeiro_nome}}', key: 'primeiro_nome' },
  { label: '{{empresa}}', key: 'empresa' },
  { label: '{{email}}', key: 'email' },
  { label: '{{telefone}}', key: 'telefone' },
  { label: '{{valor_deal}}', key: 'valor_deal' },
  { label: '{{estagio_deal}}', key: 'estagio_deal' },
  { label: '{{meu_nome}}', key: 'meu_nome' },
  { label: '{{meu_email}}', key: 'meu_email' },
];

function ToolbarBtn({
  active,
  onClick,
  children,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'p-1.5 rounded transition-colors',
        active
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}

export function EmailTemplateCreateModal({
  open,
  onOpenChange,
  onCreated,
}: EmailTemplateCreateModalProps) {
  const { toast } = useToast();
  const { createTemplate, categories, isCreating } = useEmailTemplates();

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('Geral');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-primary underline cursor-pointer' },
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[200px] px-3 py-2 text-sm text-foreground',
        'data-placeholder': 'Digite o conteúdo do template aqui...',
      },
    },
  });

  const handleInsertVariable = (key: string) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`{{${key}}}`).run();
  };

  const handleLink = () => {
    if (!editor) return;
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt('URL do link:');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const handleSave = async () => {
    if (!editor) return;
    const bodyHtml = editor.getHTML();
    if (!name.trim() || !bodyHtml.trim()) return;
    try {
      const template = await createTemplate({
        name: name.trim(),
        subject: subject.trim() || null,
        category,
        body_html: bodyHtml,
        variables: [],  // hook auto-extracts
        is_shared: true,
      });
      toast({ title: 'Template criado com sucesso!' });
      onCreated?.(template);
      handleClose();
    } catch {
      // error toast handled by hook
    }
  };

  const handleClose = () => {
    setName('');
    setSubject('');
    setCategory('Geral');
    editor?.commands.clearContent();
    onOpenChange(false);
  };

  const bodyHtml = editor?.getHTML() ?? '';
  const isSaveDisabled = !name.trim() || !bodyHtml.trim() || bodyHtml === '<p></p>' || isCreating;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogTitle>Criar novo template</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="tpl-name" className="text-sm font-medium">
              Nome do template <span className="text-destructive">*</span>
            </Label>
            <Input
              id="tpl-name"
              placeholder="Ex: Follow-up pós-demo"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Subject */}
          <div className="space-y-1.5">
            <Label htmlFor="tpl-subject" className="text-sm font-medium">
              Assunto{' '}
              <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Input
              id="tpl-subject"
              placeholder="Ex: Re: {{empresa}} — Proposta"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Category pills */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Categoria</Label>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  className={cn(
                    'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                    category === cat
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Merge variable buttons */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              Variáveis dinâmicas{' '}
              <span className="text-muted-foreground font-normal">(clique para inserir)</span>
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {MERGE_VARIABLES.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => handleInsertVariable(v.key)}
                  className="px-2 py-1 text-xs rounded border border-dashed border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/60 transition-colors font-mono"
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Body editor */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              Conteúdo do template <span className="text-destructive">*</span>
            </Label>
            {editor && (
              <div className="rounded-lg border border-gray-300 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20 bg-white transition-all duration-150">
                {/* Mini toolbar */}
                <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50/50 rounded-t-lg">
                  <ToolbarBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrito">
                    <Bold className="h-4 w-4" />
                  </ToolbarBtn>
                  <ToolbarBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Itálico">
                    <Italic className="h-4 w-4" />
                  </ToolbarBtn>
                  <ToolbarBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Sublinhado">
                    <UnderlineIcon className="h-4 w-4" />
                  </ToolbarBtn>
                  <div className="w-px h-4 bg-gray-300 mx-1" />
                  <ToolbarBtn active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista">
                    <List className="h-4 w-4" />
                  </ToolbarBtn>
                  <ToolbarBtn active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Lista numerada">
                    <ListOrdered className="h-4 w-4" />
                  </ToolbarBtn>
                  <div className="w-px h-4 bg-gray-300 mx-1" />
                  <ToolbarBtn active={editor.isActive('link')} onClick={handleLink} title="Link">
                    <LinkIcon className="h-4 w-4" />
                  </ToolbarBtn>
                </div>
                <EditorContent editor={editor} />
                <style>{`
                  .ProseMirror p.is-editor-empty:first-child::before {
                    content: attr(data-placeholder);
                    float: left;
                    color: hsl(var(--muted-foreground));
                    pointer-events: none;
                    height: 0;
                    font-size: 0.875rem;
                  }
                  .ProseMirror:focus { outline: none; }
                  .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; }
                  .ProseMirror ul { list-style-type: disc; }
                  .ProseMirror ol { list-style-type: decimal; }
                `}</style>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isCreating}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaveDisabled}>
            {isCreating ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
